import { useState, useEffect } from 'react';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/images';
import Lightbox from './Lightbox';

export default function GallerySection({ gallerySlug, title, subtitle, className = '' }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadGallery() {
      setLoading(true);
      try {
        const galleries = await fetchCollection('galleries');
        if (galleries && galleries.length > 0) {
          const match = gallerySlug 
            ? galleries.find(g => g.identifier === gallerySlug || g.slug?._ === gallerySlug)
            : galleries[0];
          if (isMounted) {
            setGallery(match || galleries[0]);
          }
        }
      } catch (err) {
        console.error('Error loading gallery:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadGallery();
    return () => { isMounted = false; };
  }, [gallerySlug]);

  if (loading) {
    return (
      <div className={`py-12 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="aspect-4/3 bg-slate-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const images = gallery.images;

  const handleOpenLightbox = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <section className={`py-14 sm:py-20 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(title || gallery.title) && (
          <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {title || gallery.title}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-600">
              {subtitle || gallery.description || 'Klik på et billede for at udforske optagelser og visuelt udtryk i stor visning.'}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {images.slice(0, 6).map((imgUrl, idx) => (
            <div
              key={idx}
              onClick={() => handleOpenLightbox(idx)}
              className="group relative aspect-4/3 overflow-hidden bg-slate-100 border border-slate-200/80 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <img
                src={getOptimizedImageUrl(imgUrl, { width: 700, height: 525, fit: 'cover' })}
                alt={`${gallery.title} - foto ${idx + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      <Lightbox
        images={images}
        initialIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </section>
  );
}
